import {RouteContext, Routes} from 'universal-router';
import {AnyRouteResult, RouteKeywords, RouterDeps} from '@/app/interfaces/routing';
import {RouteName} from '@/app/constants/routing';
import {defaultForecastDaysMode, ForecastDaysMode} from '@/app/constants/forecast';
import {getLocalOffsetFromUTCOffset} from '@/common/helpers/date/getLocalOffsetFromUTCOffset';
import * as forecastActions from '@/app/store/forecast/actions';
import {fetchMapsDataAction} from '@/app/store/maps/actions';
import {selectLanguage} from '@/app/store/config/selectors';
import {selectForecastData} from '@/app/store/forecast/selectors';
import {selectMapsData} from '@/app/store/maps/selectors';

export const createRoutes = (
	keywords: Record<RouteKeywords, string>,
	defaultLanguage: string,
	supportedLanguages: string[],
) => {
	const lngKey = supportedLanguages.join('|');
	const weatherKey = keywords.weather;
	const mapKey = keywords.map;
	const daysKey = [keywords['10days']].join('|');

	const weekdays = [
		keywords.sunday,
		keywords.monday,
		keywords.tuesday,
		keywords.wednesday,
		keywords.thursday,
		keywords.friday,
		keywords.saturday,
	];

	const weekdaysKey = weekdays.join('|');

	const daysValueMap = {
		[keywords['10days']]: ForecastDaysMode.Days10,
	};

	const routes: Routes<AnyRouteResult, RouterDeps> = [
		{
			name: RouteName.mainPage,
			// [/:language]
			path: `/{:lang(${lngKey})}?`,
			async action(context, params) {
				const {store, isHydrateMode} = context as RouteContext<AnyRouteResult, RouterDeps> & RouterDeps;
				const language = (params.lang as string | undefined) || defaultLanguage;

				// Prevent data fetching for the initial router resolving after server rendering
				if (!isHydrateMode) {
					await store.dispatch(forecastActions.fetchMainPageForecastDataAction({language}));
				} else {
					store.dispatch(forecastActions.setForecastViewParams({daysMode: ForecastDaysMode.Days7}));
				}

				return {name: RouteName.mainPage, params};
			},
		},
		{
			name: RouteName.weather,
			// [/:language]/weather/:location/[:daysMode][/:selectedDate]
			path: `{/:lang(${lngKey})}?/${weatherKey}{/:location}?{/:days(${daysKey})}?{/:selectedDate([\\d]{4}-[\\d]{2}-[\\d]{2})}?{/:selectedWeekday(${weekdaysKey})}?`,

			async action(context, params) {
				const {store} = context as RouteContext<AnyRouteResult, RouterDeps> & RouterDeps;

				// Redirect to the main page when no location defined, redirect instead 404 is used by SEO requirements
				if (!params.location) {
					return {
						name: RouteName.redirect,
						params,
						code: 301,
						cacheAge: 3600,
						to: {
							name: RouteName.mainPage,
							params,
						},
					};
				}

				const language = (params.lang as string | undefined) || defaultLanguage;
				const locationId = params.location as string;

				// Determinate selected date or weekday
				const selectedDate = params.selectedDate as string | undefined;
				let selectedWeekday: number | undefined;
				if (params.selectedWeekday) {
					selectedWeekday = weekdays.indexOf(params.selectedWeekday as string);
				}

				// Determinate days mode
				let daysMode = daysValueMap[params.days as string] || defaultForecastDaysMode;
				if (selectedDate) {
					daysMode = ForecastDaysMode.Days10;
				}

				const previousData = selectForecastData(store.getState());
				let fetchDataRequired = true;

				if (previousData?.location && previousData.forecast) {
					// Check is previous data is for the current location
					if (previousData.location.id === locationId) {
						fetchDataRequired = false;
					}

					// Check is previous data is with che current language
					if (previousData.language !== language) {
						fetchDataRequired = true;
					}

					// Handle navigating from an archived data...
					// The cache is invalid if:
					// - the selected date is specified and for it is no data in the store
					// - the selected date is not specified and there is no data for the current or next(if the days mode is tomorrow) day
					let requiredDate: string; // Date string in human readable english (Date.toDateString method used)
					if (selectedDate) {
						requiredDate = new Date(selectedDate).toDateString();
					} else {
						const now = Date.now() + getLocalOffsetFromUTCOffset(previousData.location.timezoneOffset);
						requiredDate = new Date(now).toDateString();
					}

					if (!previousData.forecast.some(day => new Date(day.date).toDateString() === requiredDate)) {
						fetchDataRequired = true;
					}
				}

				if (fetchDataRequired) {
					const result = await store.dispatch(
						forecastActions.fetchSettlementForecastDataAction({
							language,
							locationId,
							daysMode,
							selectedDate,
							selectedWeekday,
						}),
					);

					if (result.type === 'failure') {
						// Go to 404 when location is not found
						if (result.payload.reason === 'locationNotFound') {
							return {name: RouteName.notFound, originalName: RouteName.weather, params};
						}

						// Go to 404 when no any data for the required location
						if (result.payload.reason === 'noData' && !selectedDate && selectedWeekday === undefined) {
							return {name: RouteName.notFound, originalName: RouteName.weather, params};
						}

						// Redirect to the same route without date when data is invalid or no data for the date
						if (result.payload.reason === 'invalidDate' || result.payload.reason === 'noData') {
							return {
								name: RouteName.redirect,
								params,
								code: 301,
								cacheAge: 3600,
								to: {
									name: RouteName.weather,
									params: {...params, selectedDate: undefined, selectedWeekday: undefined} as any,
								},
							};
						}

						throw new Error('router resolve fail: unexpected "failure" response');
					}
				} else {
					store.dispatch(forecastActions.setForecastViewParams({daysMode, selectedDate, selectedWeekday}));
				}

				// Require an extra data in the background when switching from the main page to the settlement page mode
				if (!fetchDataRequired && previousData?.extra.type === 'mainPage') {
					store.dispatch(forecastActions.fetchSettlementExtraDataAction({language}));
				}

				return {
					name: RouteName.weather,
					params,
					data: {
						locationId,
						daysMode,
						selectedDate,
						selectedWeekday,
					},
				};
			},
		},
		{
			name: RouteName.map,
			// [/:language]/map/:location
			path: `{/:lang(${lngKey})}?/${mapKey}/:location*`,
			async action(context, params) {
				const {store} = context as RouteContext<AnyRouteResult, RouterDeps> & RouterDeps;

				// Redirect to the main page when no location defined, redirect instead 404 is used by SEO requirements
				if (!params.location) {
					return {
						name: RouteName.redirect,
						params,
						code: 301,
						cacheAge: 3600,
						to: {
							name: RouteName.mainPage,
							params,
						},
					};
				}

				const language = (params.lang as string | undefined) || defaultLanguage;
				const locationId = (params.location as string[]).join('/');

				const previousData = selectMapsData(store.getState());
				const fetchDataRequired =
					!previousData || previousData.location.id !== locationId || previousData.language !== language;

				if (fetchDataRequired) {
					// Fill the store with a required data
					const result = await store.dispatch(fetchMapsDataAction({language, locationId}));
					if (result.type === 'failure') {
						// Go to 404 when location is not found
						if (result.payload.reason === 'locationNotFound') {
							return {name: RouteName.notFound, originalName: RouteName.map, params};
						}
						throw new Error('router resolve fail: unexpected "failure" response');
					}
				}

				return {name: RouteName.map, params, data: {locationId}};
			},
		},
		{
			name: RouteName.notFound,
			path: `{/:lang(${lngKey})}?{/:path(.*)}?`,
			async action(context, params) {
				return {name: RouteName.notFound, params};
			},
		},
	];

	// Patch each router to add redirect on a language in profile mismatch
	return routes.map(route => {
		const originalAction = route.action!;
		if (route.name === RouteName.notFound) {
			return route;
		}

		route.action = async (context, params) => {
			const {store} = context as RouteContext<AnyRouteResult, RouterDeps> & RouterDeps;
			const profileLanguage = selectLanguage(store.getState());

			// Skip redirect when user did not customized its language
			if (!profileLanguage) {
				return originalAction(context, params);
			}

			const requiredRouteLanguage = profileLanguage === defaultLanguage ? undefined : profileLanguage;
			const actualRouteLanguage = params.lang as string | undefined;

			// Skip redirect if user's profile language it the same as in url just
			if (actualRouteLanguage === requiredRouteLanguage) {
				return originalAction(context, params);
			}

			// If user's profile language mismatches with the url language - redirect to require by the profile config language url
			return {
				name: RouteName.redirect,
				params,
				to: {
					name: route.name as RouteName,
					params: {
						...params,
						lang: requiredRouteLanguage as any,
					},
				},
			};
		};

		return route;
	});
};
